// Life & Wealth Solutions V24 - Activity Summary Component
// Session 8 - Added Demo Mode Support
// Shows decisions, approvals, and efficiency stats

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { isDemoMode, mockData } from '../../services/mockDataService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ActivityItem {
  activity_id: string;
  type: 'policy_bind' | 'approval' | 'rejection' | 'override';
  description: string;
  policy_id?: string;
  timestamp: string;
}

interface ActivityStats {
  policies_bound_today: number;
  policies_bound_week: number;
  approvals_today: number;
  rejections_today: number;
  average_decision_time: number; // in seconds
  automation_rate: number; // percentage
}

interface Props {
  userId: string;
}

const ActivitySummary: React.FC<Props> = ({ userId }) => {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadActivity();
  }, [userId]);

  const loadActivity = async () => {
    try {
      // Check if demo mode is enabled
      if (isDemoMode()) {
        // Use mock data
        const mockActivities = mockData.activities();
        const mockStats = mockData.activityStats();
        setActivities(mockActivities);
        setStats(mockStats);
        setError('');
        setLoading(false);
        return;
      }

      // Production mode - call actual API
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/agent12/activity-summary/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data.activities || []);
      setStats(response.data.stats || null);
      setError('');
    } catch (err: any) {
      console.error('Failed to load activity:', err);
      setError('Failed to load activity summary');
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'policy_bind': return '✅';
      case 'approval': return '👍';
      case 'rejection': return '👎';
      case 'override': return '⚠️';
      default: return '📋';
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'policy_bind': return 'bg-green-50 border-green-200';
      case 'approval': return 'bg-blue-50 border-blue-200';
      case 'rejection': return 'bg-red-50 border-red-200';
      case 'override': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading activity...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Performance Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Policies Bound Today</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.policies_bound_today}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Policies Bound This Week</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.policies_bound_week}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Approvals Today</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.approvals_today}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Rejections Today</div>
            <div className="text-2xl font-bold text-red-600 mt-1">{stats.rejections_today}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Avg Decision Time</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">{stats.average_decision_time}s</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">AI Automation Rate</div>
            <div className="text-2xl font-bold text-indigo-600 mt-1">{stats.automation_rate}%</div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">📊 Recent Activity</h3>
          <button
            onClick={loadActivity}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            🔄 Refresh
          </button>
        </div>

        {activities.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📋</div>
            <p className="text-gray-600">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div
                key={activity.activity_id}
                className={`border rounded-lg p-4 ${getActivityColor(activity.type)}`}
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{getActivityIcon(activity.type)}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                    {activity.policy_id && (
                      <p className="text-xs text-gray-500 mt-1">Policy: {activity.policy_id}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Efficiency Score Breakdown */}
      {stats && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
          <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>⚡</span>
            <span>Your Efficiency Impact</span>
          </h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">AI agents handled autonomously:</span>
              <span className="font-bold text-green-600">{stats.automation_rate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Required your authority:</span>
              <span className="font-bold text-blue-600">{100 - stats.automation_rate}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Average decision speed:</span>
              <span className="font-bold text-purple-600">{stats.average_decision_time}s</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-200">
            <p className="text-xs text-gray-600">
              💡 <strong>SaaSpocalypse Value:</strong> You're operating as a decision factory,
              only touching the high-value files. My Maestro routes $15k+ opportunities directly to you.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitySummary;
